import React, { useContext, useEffect, useRef, useState } from 'react'
import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import { exists, window } from 'browser-monads'
import exitIntent from 'exit-intent'
import { graphql } from 'gatsby'
import {
  ArticleSource,
  HelloBar,
  ScrollToTopButton,
} from '../../../common/components'
import { Row } from '../../../common/lib'
import '../../../common/scss/cheerio.scss'
import styles from '../../../common/scss/content.module.scss'
import {
  formatDate,
  replaceYear,
  useIsIntersectingElement,
} from '../../../common/utils'

import FaqSchema from '../../../common/components/FaqSchema'
import { GENERIC_CONSTANT } from '../../../common/utils/constants'
import ArticleStickyHeader from './article/ArticelSticyHeader'
import ArticleContentSection from './article/ArticleContentSection'
import ArticlePageHelmet from './article/ArticlePageHelmet'
import ArticleSidebar from './article/ArticleSidebar'
import ArticleToc from './article/ArticleToc'
import { editArticle } from '../../../common/utils/editArticle'
import '../../../common/css/aptitudeStyle.css'
import SeeAlso from '../components/SeeAlso/SeeAlso'
import replaceText from '../../../common/utils/replaceText'
import TestHQSticky from '../components/JobTestSticky/TestHQSticky'
import JobTestSticky from '../components/JobTestSticky'
import showTestHQBanner from '../helpers/showBanner'
export default function ArticleTemplate({
  data,
  location,
  pageContext: { pathname },
}) {
  const tokens = data.allDatoCmsToken.edges
  // Refs
  const hiddenHrRefs = useRef([])
  const lastUpdatedRef = useRef(null)
  // States
  const [stickyHeaderShown, setStickyHeaderShown] = useState('')
  const [eventListeners, setEventListeners] = useState(false)
  const [pageIsScrolled, setPageIsScrolled] = useState(false)

  // constant variables
  const article = data.datoCmsPrtArticle

  const isAptitudeTest = location.pathname.match('aptitude-test')
  const updatedAt = formatDate(article.meta?.updatedAt, 'article')
  const canonicalUrl = `${process.env.GATSBY_SITE_BASE_URL}${location.pathname}`
  const isScrollToTopShown = !useIsIntersectingElement(lastUpdatedRef.current)

  const quizItem = article?.modularContent.find(
    (item) => item?.internal?.type === 'DatoCmsQuiz',
  )

  const faqs = article?.modularContent?.filter(
    (content) => content?.internal?.type === 'DatoCmsFaqItem',
  )

  const helloBarInfo = {
    url:
      article.helloBarUrl ||
      article.heroButtonUrl ||
      (isAptitudeTest ? GENERIC_CONSTANT.hellobar_button_url : null),
    buttonText:
      replaceText(tokens, article.helloBarButtonText) ||
      (isAptitudeTest
        ? `Take a ${replaceText(tokens, article.title)} test!`
        : null),
    text:
      replaceText(tokens, article.helloBarText) ||
      (isAptitudeTest ? GENERIC_CONSTANT.practice_aptitude_text : null),
    visible: !!(
      article.helloBarText ||
      (location.pathname.match('aptitude-test') &&
        !location.pathname.endsWith('aptitude-tests'))
    ),
  }

  if (window.dataLayer) {
    window.dataLayer.push({ article, disableAds: article.disableAds })
  }

  if (window.dataLayer) {
    window.dataLayer.push({ event: 'componentDidMount' })
  }

  // Handling scroll on browser to show StickyHeader
  useScrollPosition(({ prevPos, currPos }) => {
    if (exists(window)) {
      if (!stickyHeaderShown && currPos.y < -650) {
        setStickyHeaderShown(true)
      }
      if (stickyHeaderShown && currPos.y > -650) {
        setStickyHeaderShown('')
      }
      if (
        prevPos.y !== 0 &&
        prevPos.y !== currPos.y &&
        location.pathname.includes('aptitude-tests')
      ) {
        if (!pageIsScrolled) {
          exitIntent({
            threshold: 18,
            maxDisplays: 1,
            eventThrottle: 500,
            onExitIntent: () => {},
          })
          setPageIsScrolled(true)
        }
      }
    }
  })

  // useEffect Section
  useEffect(() => {
    if (!eventListeners) {
      const handlePressedKey = (event) => {
        if (event.ctrlKey && event.key === 'b') {
          editArticle(935478, article.originalId)
        }
      }
      window.addEventListener('keydown', handlePressedKey)
      setEventListeners(true)
    }
  }, [
    isAptitudeTest,
    eventListeners,
    article.originalId,
    article.heroButtonUrl,
    article.helloBarUrl,
    article.helloBarButtonText,
    article.title,
    article.helloBarText,
    location.pathname,
  ])

  useEffect(() => {
    document.documentElement.lang = article.language
  }, [article.language])

  const ShowHelloBar = (
    <>
      {helloBarInfo &&
        helloBarInfo.url &&
        helloBarInfo.text &&
        helloBarInfo.buttonText && (
          <HelloBar
            location={location}
            url={helloBarInfo.url}
            buttonText={helloBarInfo.buttonText}
            text={helloBarInfo.text}
          />
        )}
    </>
  )

  const ShowArticle = (
    <div
      className={`container-xxl ${styles.paddingTop} ${styles.containerBackground}`}
    >
      <ArticleStickyHeader
        article={article}
        location={location}
        stickyHeaderShown={stickyHeaderShown}
      />
      {/* {showTestHQBanner(`/${article.slug}`) ? (
        <TestHQSticky
          buyLink="https://l.linklyhq.com/l/5rPQ"
          freeLink="https://l.linklyhq.com/l/5rPQ"
          style={{
            position: 'fixed',
            left: 0,
          }}
        />
      ) : (
        <JobTestSticky buyLink="https://www.jobtestprep.co.uk/shl-practice-tests#PRT" />
      )} */}

      <ArticlePageHelmet
        quizItem={quizItem}
        article={article}
        canonicalUrl={canonicalUrl}
        tokens={tokens}
      />
      {/* <CoverImage coverImage={article.coverImage} title={article.title} /> */}
      <Row>
        <ArticleToc
          article={article}
          faqs={faqs}
          location={location}
          tokens={tokens}
        />
        <ArticleContentSection
          faqs={faqs}
          article={article}
          location={location}
          pathname={pathname}
          updatedAt={updatedAt}
          hiddenHrRefs={hiddenHrRefs}
          lastUpdatedRef={lastUpdatedRef}
          tokens={tokens}
        />
        <ArticleSidebar
          location={location}
          article={article}
          faqs={faqs}
          tokens={tokens}
        />
      </Row>
      <FaqSchema faqs={faqs} />
      {article.sources.length > 0 && (
        <ArticleSource sources={article.sources} />
      )}
      {article.seeAlso && <SeeAlso article={article} tokens={tokens} />}
      <ScrollToTopButton isScrollToTopShown={isScrollToTopShown} />
    </div>
  )

  return (
    <>
      {/* {ShowHelloBar} */}
      {ShowArticle}
    </>
  )
}

export const query = graphql`
  query ($id: String!) {
    datoCmsPrtArticle(id: { eq: $id }) {
      languageAlternativesHreflang {
        alternateUrl
        language
      }
      originalId
      title
      slug
      sources {
        publication
        publicationYear
        author
        link
      }
      metaTitle
      metaDescription
      showJtpBuyBox
      heroDisclaimer
      meta {
        createdAt
        updatedAt
        publishedAt
        firstPublishedAt
      }
      language
      disableAds
      reviewedBy {
        name
      }
      author {
        name
        picture {
          gatsbyImageData(
            layout: FIXED
            width: 50
            height: 50
            imgixParams: {
              auto: "compress, format, enhance"
              fit: "facearea"
              w: "50"
              h: "50"
              facepad: 3
            }
          )
        }
      }
      helloBarButtonText
      helloBarText
      helloBarUrl
      heroText
      heroButtonText
      heroButtonUrl
      heroButtonUrl2
      coverImage {
        gatsbyImageData(
          imgixParams: {
            auto: "format, compress, enhance"
            fit: "crop"
            h: "400"
            w: "1116"
          }
          layout: FULL_WIDTH
          sizes: "(max-width: 1116px) calc(100vw - 120px), 1116px"
          breakpoints: [200, 400, 900, 1116]
        )
        ogImage: url(imgixParams: { auto: "enhance", w: "1200", h: "630" })
      }
      insetImage {
        gatsbyImageData(
          imgixParams: {
            auto: "format, compress, enhance"
            fit: "crop"
            h: "300"
            w: "300"
          }
          sizes: "(max-width: 300) calc(100vw - 120px), 300"
          breakpoints: [200, 300, 400, 900, 1116]
        )
        ogImage: url(imgixParams: { auto: "enhance", w: "300", h: "300" })
      }
      seeAlso {
        title
        slug
        shortUrl
        coverImage {
          gatsbyImageData(
            imgixParams: { auto: "compress, format, enhance", fit: "crop" }
            height: 150
            width: 220
          )
        }
        insetImage {
          gatsbyImageData(
            imgixParams: { auto: "compress, format, enhance", fit: "crop" }
          )
        }
        category {
          id
          title
          slug
          category {
            id
            title
            slug
          }
        }
      }
      category {
        title
        slug
        category {
          title
          slug
        }
      }
      modularContent {
        ... on DatoCmsProCon {
          internal {
            type
          }
          proNode {
            childMarkdownRemark {
              id
              html
            }
          }
          conNode {
            childMarkdownRemark {
              id
              html
            }
          }
        }
        ... on DatoCmsExampleQuestion {
          internal {
            type
          }
          questionNode {
            childMarkdownRemark {
              html
            }
          }
          answerExplanationNode {
            childMarkdownRemark {
              html
            }
          }
        }
        ... on DatoCmsMarkdown {
          internal {
            type
          }
          markdownNode {
            childMarkdownRemark {
              html
              tableOfContents(maxDepth: 3)
            }
          }
        }
        ... on DatoCmsBigQuote {
          internal {
            type
          }
          bodyNode {
            childMarkdownRemark {
              html
            }
          }
          style
        }
        ... on DatoCmsProduct {
          internal {
            type
          }
          name
          subheading
          label
          bodyString: body
          image {
            gatsbyImageData(
              imgixParams: { auto: "format, compress, enhance", fit: "crop" }
              layout: FULL_WIDTH
              placeholder: BLURRED
              sizes: "(max-width: 991px) calc(100vw - 160px), 991px"
              breakpoints: [200, 508, 721, 991]
            )
            url(
              imgixParams: {
                q: 70
                w: "400"
                auto: "[compress, format, enhance]"
              }
            )
          }
          buttonText
          buttonUrl
        }
        ... on DatoCmsHtml {
          internal {
            type
          }
          html
        }
        ... on DatoCmsCallToActionButton {
          internal {
            type
          }
          link
          text
        }
        ... on DatoCmsRawHtmlScript {
          internal {
            type
          }
          rawHtml
        }
        ... on DatoCmsQuiz {
          internal {
            type
          }
          test {
            id
            name
            url
            urlCanonical
            description
            introText
            group
            timeLimit
            passPercentage
            metaTitle
            metaDescription
            questions {
              id
              infoNode {
                childMarkdownRemark {
                  html
                }
              }
              questionNode {
                childMarkdownRemark {
                  html
                  tableOfContents(maxDepth: 3)
                }
              }
              explanationNode {
                childMarkdownRemark {
                  html
                  tableOfContents(maxDepth: 3)
                }
              }
              answersJson
            }
          }
        }
        ... on DatoCmsImage {
          internal {
            type
          }
          image {
            gatsbyImageData(
              layout: FULL_WIDTH
              placeholder: BLURRED
              breakpoints: [200, 508, 721, 991, 1116]
            )
            url(
              imgixParams: {
                q: 70
                w: "1000"
                auto: "[format, compress, enhance]"
              }
            )
            title
            alt
          }
        }
        ... on DatoCmsTop10item {
          title
          ctaUrl
          published
          bodyNode {
            childMarkdownRemark {
              html
            }
          }
          internal {
            type
          }
        }
        ... on DatoCmsFaqItem {
          id
          answerNode {
            childMarkdownRemark {
              html
            }
          }
          question
          internal {
            type
          }
        }
        ... on DatoCmsTop10item {
          modularContent {
            ... on DatoCmsProCon {
              internal {
                type
              }
              proNode {
                childMarkdownRemark {
                  id
                  html
                }
              }
              conNode {
                childMarkdownRemark {
                  id
                  html
                }
              }
            }
            ... on DatoCmsMarkdown {
              id
              internal {
                type
              }
              markdownNode {
                childMarkdownRemark {
                  html
                }
              }
            }
          }
          title
          ctaUrl
          published
          disclaimer
          bodyNode {
            childMarkdownRemark {
              html
            }
          }
          internal {
            type
          }
        }
        ... on DatoCmsHowto {
          title
          intro {
            markdownNode {
              childMarkdownRemark {
                html
                rawMarkdownBody
              }
            }
          }
          internal {
            type
          }
          howtosteps {
            title
            internal {
              type
            }
            body {
              ... on DatoCmsCallToActionButton {
                internal {
                  type
                }
                link
                text
              }
              ... on DatoCmsHtml {
                internal {
                  type
                }
                html
              }
              ... on DatoCmsImage {
                internal {
                  type
                }
                image {
                  gatsbyImageData(
                    layout: FULL_WIDTH
                    placeholder: BLURRED
                    breakpoints: [200, 508, 721, 991, 1116]
                  )
                  url(
                    imgixParams: {
                      q: 70
                      w: "1000"
                      auto: "[format, compress, enhance]"
                    }
                  )
                  title
                  alt
                }
              }
              ... on DatoCmsMarkdown {
                id
                internal {
                  type
                }
                markdownNode {
                  childMarkdownRemark {
                    html
                    rawMarkdownBody
                  }
                }
              }
              ... on DatoCmsHtml {
                internal {
                  type
                }
                html
              }
              ... on DatoCmsRawHtmlScript {
                internal {
                  type
                }
                rawHtml
              }
              ... on DatoCmsBigQuote {
                internal {
                  type
                }
                bodyNode {
                  childMarkdownRemark {
                    html
                    tableOfContents(maxDepth: 3)
                  }
                }
                style
              }
            }
            id
            image {
              gatsbyImageData(
                layout: FULL_WIDTH
                placeholder: BLURRED
                breakpoints: [200, 508, 721, 991, 1116]
              )
              url(
                imgixParams: {
                  q: 70
                  w: "1000"
                  auto: "[format, compress, enhance]"
                }
              )
              title
              alt
            }
          }
        }
        ... on DatoCmsTable {
          internal {
            type
          }
          table {
            tr {
              ... on DatoCmsTableColumn {
                internal {
                  type
                }
                colspan
                th
                td {
                  ... on DatoCmsRawHtmlScript {
                    internal {
                      type
                    }
                    rawHtml
                  }
                  ... on DatoCmsMarkdown {
                    id
                    internal {
                      type
                    }
                    markdownNode {
                      childMarkdownRemark {
                        html
                        rawMarkdownBody
                      }
                    }
                  }
                  ... on DatoCmsHtml {
                    internal {
                      type
                    }
                    html
                  }
                  ... on DatoCmsCallToActionButton {
                    internal {
                      type
                    }
                    link
                    text
                  }
                }
              }
            }
          }
        }
      }
    }
    allDatoCmsToken {
      edges {
        node {
          key
          value
          valueMarkdown {
            markdownNode {
              childMarkdownRemark {
                html
              }
            }
          }
        }
      }
    }
  }
`
